import { effect, signal } from '@preact/signals';
import { compareVersions } from '@vm/shared';
import { attribution } from '../attribution';
import { audio } from '../../game/audio/Audio';
import { clearSession, deviceId, errorCode, errorMessage, hasSession, isNetworkError, isSessionRejected, onAuthLost, onBanned, onUpdateRequired, refreshToken, setSession, writeLocal } from '../api/http';
import { api } from '../api/routes';
import { social } from '../api/social';
import { initPortalSdk, portalLoadingDone } from '../crazygames';
import { platform, portal } from '../platform';
import { loadCatalog, loadInbox, loadLobbyData, loadMe, refreshMe, setMe, startPurchaseDelivery } from './actions';
import { generateName } from './names';
import { home } from './nav';
import {
  cachedCatalog,
  cachedMe,
  clearProfileCaches,
  isUnder13,
  localName,
  offlineProfile,
  onboardedFlag,
  setOnboardedFlag,
  storeBirthYear,
  storedBirthYear,
  storeLocalName,
} from './offline';
import { launch, startTutorial } from './play';
import { loadFriends, loadMyClan, loadParty } from './socialActions';
import {
  achievements,
  battlePass,
  catalog,
  chatLog,
  chatUnread,
  conn,
  friends,
  inbox,
  me,
  myClan,
  party,
  presenceById,
  queue,
  quests,
  toast,
} from './store';
import { track } from './telemetry';

export type Phase = 'boot' | 'onboarding' | 'main' | 'banned';
export type OnboardingStep = 'age' | 'welcome' | 'login' | 'name' | 'tutorial';

export const phase = signal<Phase>('boot');
export const bootMessage = signal('Loading…');
export const onboardingStep = signal<OnboardingStep>('age');
export const onboardingNote = signal<string | null>(null);
export const bannedMessage = signal('');
/** Set when this app is older than live-ops allows (`client.versions`): the update screen replaces every phase. */
export const updateMessage = signal('');

const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

function resetStores(): void {
  me.value = null;
  quests.value = null;
  battlePass.value = null;
  achievements.value = null;
  friends.value = null;
  presenceById.value = {};
  party.value = null;
  myClan.value = null;
  chatLog.value = {};
  chatUnread.value = {};
  inbox.value = null;
  queue.value = { state: 'idle' };
}

function startOnlineServices(): void {
  conn.value = 'online';
  social.start();
  loadLobbyData();
  void loadFriends().catch(() => {});
  void loadMyClan().catch(() => {});
  void loadInbox().catch(() => {});
  void loadParty().catch(() => {});
  startPurchaseDelivery();
  track('session_start', { platform, restricted: !!me.value?.restricted });
}

/**
 * Web portals (CrazyGames) only serve players 13 and over, and their players never see the age screen. This is the
 * youngest birth year the API treats as unrestricted, with a year to spare for clocks that disagree about New Year.
 */
const portalBirthYear = (): number => new Date().getUTCFullYear() - 15;

/** The birth year the guest account is created with: the stored answer to the age screen, or the portal's fixed one. */
const guestBirthYear = (): number | null => (portal ? portalBirthYear() : storedBirthYear());

async function guestAuth(year: number): Promise<void> {
  const res = await api.auth.guest({
    deviceId,
    birthYear: year,
    locale: navigator.language,
    platform,
    attribution,
    appVersion: __APP_VERSION__,
    touch: navigator.maxTouchPoints > 0,
  });
  setSession(res);
  setMe(res.me);
}

function catalogFallback(): void {
  if (!catalog.value) catalog.value = cachedCatalog();
}

/** Decides where the player lands after authenticating. */
function landAfterAuth(freshAccount: boolean): void {
  startOnlineServices();
  if (portal) {
    portalLand();
    return;
  }
  const m = me.value;
  if (freshAccount || (!onboardedFlag() && m && !m.renamed && !m.tutorialDone)) {
    onboardingStep.value = 'name';
    phase.value = 'onboarding';
    return;
  }
  setOnboardedFlag(true);
  home();
  phase.value = 'main';
}

/** Portal builds have no onboarding: straight to the menus, and a first-time player straight into Boot Camp. */
function portalLand(): void {
  setOnboardedFlag(true);
  home();
  phase.value = 'main';
  maybeBootCamp();
}

function maybeBootCamp(): void {
  if (me.value && !me.value.tutorialDone && !launch.value) startTutorial();
}

/** Portal builds: the guest account is made without asking anything (no age screen, call sign or login). */
async function portalGuest(): Promise<void> {
  bootMessage.value = 'Setting up your marine…';
  const year = portalBirthYear();
  storeBirthYear(year);
  try {
    await guestAuth(year);
    catalogFallback();
    if (!catalog.value) await loadCatalog().catch(() => {});
    startOnlineServices();
    portalLand();
  } catch (e) {
    if (errorCode(e) === 'banned') {
      bannedMessage.value = errorMessage(e);
      phase.value = 'banned';
      return;
    }
    // No connection, a restart, a refused origin: play offline (practice and Boot Camp work) and keep retrying quietly.
    enterOffline();
  }
}

function enterOffline(): void {
  conn.value = 'offline';
  catalogFallback();
  const year = guestBirthYear();
  const cached = cachedMe();
  me.value = cached ?? offlineProfile(localName() ?? generateName(), year !== null ? isUnder13(year) : true);
  home();
  phase.value = 'main';
  if (hasSession() || portal) scheduleOnlineRetry(0);
  if (portal) maybeBootCamp();
}

let onlineRetry: ReturnType<typeof setTimeout> | null = null;

/** Signed in but offline (no connection, or the API restarting for a release): quietly retry, 5 s doubling to a minute. */
function scheduleOnlineRetry(attempt: number): void {
  if (onlineRetry) clearTimeout(onlineRetry);
  onlineRetry = setTimeout(() => {
    onlineRetry = null;
    if (conn.value !== 'offline' || (!hasSession() && !portal)) return;
    void retryOnline(true).then((ok) => {
      if (!ok && conn.value === 'offline') scheduleOnlineRetry(attempt + 1);
    });
  }, Math.min(60_000, 5_000 * 2 ** attempt));
}

export async function boot(): Promise<void> {
  if (portal) await initPortalSdk();
  bootMessage.value = 'Contacting HQ…';
  const catalogReady = loadCatalog().catch(() => catalogFallback());
  const wait = (): Promise<unknown> => Promise.race([catalogReady, delay(4000)]);
  if (hasSession()) {
    try {
      bootMessage.value = 'Loading your profile…';
      await loadMe();
      await wait();
      landAfterAuth(false);
      return;
    } catch (e) {
      if (errorCode(e) === 'banned') {
        bannedMessage.value = errorMessage(e);
        phase.value = 'banned';
        return;
      }
      // Only a session the API turned down ends here. During a release the API answers 502/503 for a
      // few seconds: the game opens offline, keeps the session and reconnects by itself.
      if (!isSessionRejected(e)) {
        enterOffline();
        return;
      }
      clearSession();
    }
  }
  if (portal) {
    await portalGuest();
    return;
  }
  const year = storedBirthYear();
  if (year !== null) {
    if (!onboardedFlag()) {
      // Returning mid-onboarding: create the guest again with the answer already given.
      try {
        bootMessage.value = 'Setting up your marine…';
        await guestAuth(year);
        await wait();
        landAfterAuth(true);
      } catch (e) {
        if (isNetworkError(e)) enterOffline();
        else {
          onboardingStep.value = 'welcome';
          onboardingNote.value = errorMessage(e);
          phase.value = 'onboarding';
        }
      }
      return;
    }
    // Onboarded before but the session is gone (logged out / expired): let them choose.
    onboardingStep.value = 'welcome';
    phase.value = 'onboarding';
    return;
  }
  await Promise.race([catalogReady, delay(1500)]);
  onboardingStep.value = 'age';
  phase.value = 'onboarding';
}

// Onboarding ------------------------------------------------------------------------------------------------

/** Age gate answer. Creates the guest account (or an offline profile when unreachable). */
export async function submitBirthYear(year: number): Promise<void> {
  storeBirthYear(year);
  await continueAsGuest(true);
}

export async function continueAsGuest(fresh: boolean): Promise<void> {
  const year = storedBirthYear();
  if (year === null) {
    onboardingStep.value = 'age';
    return;
  }
  try {
    await guestAuth(year);
    catalogFallback();
    if (!catalog.value) await loadCatalog().catch(() => {});
    startOnlineServices();
    if (fresh || !me.value?.renamed) {
      onboardingStep.value = 'name';
    } else {
      setOnboardedFlag(true);
      phase.value = 'main';
    }
  } catch (e) {
    if (!isNetworkError(e)) {
      onboardingNote.value = errorMessage(e);
      if (errorCode(e) === 'banned') {
        bannedMessage.value = errorMessage(e);
        phase.value = 'banned';
      }
      return;
    }
    conn.value = 'offline';
    catalogFallback();
    me.value = offlineProfile(localName() ?? generateName(), isUnder13(year));
    onboardingStep.value = 'name';
  }
}

/** Name step: online accounts rename on the server; offline profiles keep it locally. */
export async function chooseName(name: string): Promise<boolean> {
  const m = me.value;
  if (!m) return false;
  if (m.name === name) return true;
  if (conn.value === 'offline') {
    storeLocalName(name);
    me.value = { ...m, name };
    return true;
  }
  try {
    const res = await api.me.update({ name });
    me.value = { ...m, ...(res && typeof res === 'object' ? res : {}), name: (res as { name?: string } | null)?.name ?? name, renamed: true };
    storeLocalName(name);
    return true;
  } catch (e) {
    onboardingNote.value = errorMessage(e);
    return false;
  }
}

export function finishOnboarding(): void {
  setOnboardedFlag(true);
  onboardingNote.value = null;
  home();
  phase.value = 'main';
}

// Accounts ---------------------------------------------------------------------------------------------------

export async function login(email: string, password: string): Promise<boolean> {
  try {
    const res = await api.auth.login({ email: email.trim(), password, deviceId });
    social.stop();
    resetStores();
    setSession(res);
    setMe(res.me);
    catalogFallback();
    setOnboardedFlag(true);
    startOnlineServices();
    home();
    phase.value = 'main';
    audio.play('ui_buy');
    toast(`Welcome back, ${res.me.name}!`, { kind: 'success' });
    return true;
  } catch (e) {
    toast(errorCode(e) === 'unauthorized' ? 'Wrong email or password' : "Couldn't log in", { kind: 'error', body: errorCode(e) === 'unauthorized' ? undefined : errorMessage(e) });
    return false;
  }
}

export async function registerEmail(email: string, password: string): Promise<boolean> {
  try {
    const res = await api.auth.register({ email: email.trim(), password });
    if (res && res.accessToken && res.refreshToken) setSession({ accessToken: res.accessToken, refreshToken: res.refreshToken, expiresIn: res.expiresIn ?? 900 });
    if (res?.me) setMe(res.me);
    else await refreshMe();
    if (me.value && !me.value.email) me.value = { ...me.value, email: email.trim(), isGuest: false };
    toast('Account secured!', { kind: 'success', body: 'You can now log in with this email on any device.' });
    return true;
  } catch (e) {
    toast("Couldn't link that email", { kind: 'error', body: errorMessage(e) });
    return false;
  }
}

function clearLocalAccount(full: boolean): void {
  clearSession();
  social.stop();
  resetStores();
  clearProfileCaches();
  setOnboardedFlag(false);
  writeLocal('vm.localName', null);
  if (full) storeBirthYear(null);
}

export async function logout(): Promise<void> {
  try {
    await api.auth.logout(refreshToken());
  } catch {
    /* the session is dropped locally either way */
  }
  clearLocalAccount(false);
  if (portal) {
    await portalGuest();
    return;
  }
  onboardingStep.value = storedBirthYear() !== null ? 'welcome' : 'age';
  phase.value = 'onboarding';
}

export async function deleteAccount(): Promise<boolean> {
  try {
    await api.me.remove();
  } catch (e) {
    toast("Couldn't delete your account", { kind: 'error', body: errorMessage(e) });
    return false;
  }
  clearLocalAccount(true);
  toast('Your account was deleted', { kind: 'info' });
  if (portal) {
    await portalGuest();
    return true;
  }
  onboardingStep.value = 'age';
  phase.value = 'onboarding';
  return true;
}

/** Offline badge "Retry" (and the quiet automatic retry): reconnects and signs in (creating the guest if needed). */
export async function retryOnline(quiet = false): Promise<boolean> {
  try {
    await loadCatalog();
  } catch (e) {
    if (!quiet) toast('Still offline', { kind: 'warning', body: errorMessage(e) });
    return false;
  }
  try {
    const offlineName = me.value?.id === 'offline' ? me.value.name : null;
    if (hasSession()) await loadMe();
    else {
      const year = guestBirthYear();
      if (year === null) {
        onboardingStep.value = 'age';
        phase.value = 'onboarding';
        return true;
      }
      await guestAuth(year);
      if (offlineName && me.value && !me.value.renamed) {
        try {
          await api.me.update({ name: offlineName });
          me.value = { ...me.value, name: offlineName, renamed: true };
        } catch {
          /* keep the server-assigned name */
        }
      }
    }
    startOnlineServices();
    toast('Back online!', { kind: 'success' });
    return true;
  } catch (e) {
    if (errorCode(e) === 'banned') {
      bannedMessage.value = errorMessage(e);
      phase.value = 'banned';
      return false;
    }
    if (!quiet) toast('Still offline', { kind: 'warning', body: errorMessage(e) });
    return false;
  }
}

// Global listeners ---------------------------------------------------------------------------------------------

onAuthLost(() => {
  social.stop();
  if (launch.value) return;
  resetStores();
  if (portal) {
    // The device id finds the same guest again; no screen to show.
    void portalGuest();
    return;
  }
  onboardingStep.value = 'welcome';
  onboardingNote.value = 'Your session ended. Continue as a guest or log in.';
  phase.value = 'onboarding';
});

onBanned((msg) => {
  bannedMessage.value = msg || 'This account has been suspended for breaking the rules.';
  social.stop();
  phase.value = 'banned';
});

function requireUpdate(msg: string): void {
  updateMessage.value = msg || 'A new version of Voxel Marines is out. Update to keep playing online.';
  social.stop();
}

onUpdateRequired(requireUpdate);

// The catalog carries the minimum versions, so an outdated app notices at boot before any refused call.
effect(() => {
  const v = catalog.value?.clientVersions;
  const min = v?.min[platform];
  if (v && min && compareVersions(__APP_VERSION__, min) < 0 && !updateMessage.peek()) requireUpdate(v.message);
});

// The portal wants to know when the first screen is up.
if (portal) {
  effect(() => {
    if (phase.value !== 'boot') portalLoadingDone();
  });
}
